import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NO_AGENT_IMAGE } from '@rma/generic/core/images';
import { INDIVIDUAL, SALES } from '@rma/generic/domain/types';
import { MapsService } from '@rma/generic/maps/api';
import {
  type ApiMapMarker,
  type MapMarkerStatus,
  type MapMarkerType,
  type MapRequest,
  type RmaMapMarker,
  type RmaMapMarkerDetail,
  SALES_HISTORY_FROM,
} from '@rma/generic/maps/domain';
import { mapApiMapMarkerToRmaMapMarker, MapMarkerService } from '@rma/generic/maps/util';
import { appendToRmaStaticImageUrl } from '@rma/generic/ng/util';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { CampaignsApiService, mapCampaignSummaryToPropertyTile } from '@rma/public/campaigns/public/api-campaigns';
import type { AgentComparison } from '@rma/public/compare-agent/private-api/da-compare-agent';
import type { ProfileTheme, SelectionShape } from '@rma/public/generic/domain/types';
import { PlatformService } from '@rma/public/generic/util/environment';
import type { MarkerInfoType } from '@rma/public/maps/domain';
import { ReviewApiService } from '@rma/public/reviews/public/api';
import { distinctUntilChanged, filter, map, Observable, of, pipe, switchMap, tap } from 'rxjs';
import type { AgentToggleDetails } from '../ui-agent-toggle-row/agent-toggle-row.component';

export type CompareAgentsMapAgent = Pick<AgentComparison, 'agentCode' | 'agentName'> & { theme?: Pick<ProfileTheme, 'avatarImage'> };

export type MapMarkersStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface AgentMapMarker extends RmaMapMarker<MarkerInfoType> {
  agentCode: string;
}

export interface CompareAgentsMapState {
  agents: readonly CompareAgentsMapAgent[];
  status: MapMarkersStatus;
  loaded: boolean;
  hiddenAgentCodes: string[];
  markers: AgentMapMarker[];
  boundsVersion: number;
  markerType: MapMarkerType;
  shape: SelectionShape | undefined;
  gated: boolean;
}

export interface LoadRequest {
  agents: readonly CompareAgentsMapAgent[];
  gated: boolean;
  visible: boolean;
}

type MarkersRequest = Pick<CompareAgentsMapState, 'agents' | 'markerType' | 'shape'>;

const initialState: CompareAgentsMapState = {
  agents: [],
  status: 'idle',
  loaded: false,
  hiddenAgentCodes: [],
  markers: [],
  boundsVersion: 0,
  markerType: 'CompletedListing',
  shape: undefined,
  gated: false,
};

const ACTIVE_Z_INDEX = 800;
const AVATAR_IMAGE_CLASSES = ['h-10', 'w-10', 'rounded-full', 'object-cover', 'shadow-md'];
const AVATAR_RING_CLASS = 'pointer-events-none absolute inset-0 rounded-full border-2 border-white';

const toMapRequest = ({ agents, markerType, shape }: MarkersRequest): MapRequest => ({
  markerType,
  verticalType: SALES,
  profileIdentifiers: agents.map(({ agentCode }) => ({ profileType: INDIVIDUAL, profileCode: agentCode })),
  ...(markerType === 'CompletedListing' ? { from: SALES_HISTORY_FROM } : {}),
  ...(shape ? { shape } : {}),
});

const agentsOf = (apiMarker: ApiMapMarker, agents: readonly CompareAgentsMapAgent[]): CompareAgentsMapAgent[] =>
  agents.filter(({ agentCode }) =>
    (apiMarker.profileIdentifiers ?? []).some(({ profileType, profileCode }) => profileType === INDIVIDUAL && profileCode === agentCode),
  );

const sameAgentCodes = (previous: readonly CompareAgentsMapAgent[], current: readonly CompareAgentsMapAgent[]): boolean =>
  previous.length === current.length && previous.every(({ agentCode }) => current.some((agent) => agent.agentCode === agentCode));

const withStatus = (markers: AgentMapMarker[], id: string, status: MapMarkerStatus): AgentMapMarker[] =>
  markers.map((marker, index) =>
    marker.detail.id === id
      ? { ...marker, zIndex: status === 'active' ? ACTIVE_Z_INDEX : index, detail: { ...marker.detail, status, hovered: false } }
      : marker,
  );

const withInfo = (markers: AgentMapMarker[], id: string, info: MarkerInfoType): AgentMapMarker[] =>
  markers.map((marker) => (marker.detail.id === id ? { ...marker, detail: { ...marker.detail, info } } : marker));

export const CompareAgentsMapStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    visibleMarkers: computed(() => store.markers().filter(({ agentCode }) => !store.hiddenAgentCodes().includes(agentCode))),
    activeMarker: computed(
      () =>
        store
          .markers()
          .find(({ agentCode, detail }) => detail.status === 'active' && !!detail.info && !store.hiddenAgentCodes().includes(agentCode)) ??
        null,
    ),
    loading: computed(() => store.status() === 'loading'),
    failed: computed(() => store.status() === 'failed'),
    // google maps centres on its own default until markers give it bounds, so the map stays unmounted until the first answer
    initialising: computed(() => !store.loaded()),
    resetBounds: computed(() => (store.boundsVersion() ? `${store.boundsVersion()}` : undefined)),
    toggles: computed((): AgentToggleDetails[] =>
      store.agents().map(({ agentCode, agentName, theme }) => ({
        agentCode,
        agentName,
        avatarImage: theme?.avatarImage,
        checked: !store.hiddenAgentCodes().includes(agentCode),
      })),
    ),
  })),
  withComputed((store) => ({
    activeAgentCode: computed(() => store.activeMarker()?.agentCode ?? null),
    empty: computed(() => store.status() === 'ready' && store.markers().length === 0),
  })),
  withMethods((store) => {
    const platformService = inject(PlatformService);
    const mapsService = inject(MapsService);
    const mapMarkerService = inject(MapMarkerService);
    const campaignsApiService = inject(CampaignsApiService);
    const reviewApiService = inject(ReviewApiService);
    const rmaStaticImageUrl = inject(appendToRmaStaticImageUrl);

    const refitBounds = (state: CompareAgentsMapState) => ({ boundsVersion: state.boundsVersion + 1 });

    const buildAvatarContent = (agent: CompareAgentsMapAgent): Node => {
      const avatarUrl = agent.theme?.avatarImage || rmaStaticImageUrl(NO_AGENT_IMAGE);
      const content = mapMarkerService.getContent(avatarUrl, undefined, AVATAR_RING_CLASS);
      const image = content instanceof HTMLImageElement ? content : content?.querySelector('img');
      image?.classList.add(...AVATAR_IMAGE_CLASSES);

      return content;
    };

    const toAgentMarker = (apiMarker: ApiMapMarker, agent: CompareAgentsMapAgent, index: number): AgentMapMarker => {
      const marker = mapApiMapMarkerToRmaMapMarker<MarkerInfoType>(apiMarker, SALES, {
        icon: agent.theme?.avatarImage ?? '',
        gmpClickable: true,
        zIndex: index,
        content: buildAvatarContent(agent),
      });

      return {
        ...marker,
        agentCode: agent.agentCode,
        detail: { ...marker.detail, id: `${apiMarker.combinedHash}-${agent.agentCode}` },
      };
    };

    const loadMarkers = (request: MarkersRequest): Observable<AgentMapMarker[] | null> =>
      mapsService.queryMapMarkers(toMapRequest(request)).pipe(
        map((response) =>
          (response.markers ?? []).flatMap((apiMarker, index) =>
            agentsOf(apiMarker, request.agents).map((agent) => toAgentMarker(apiMarker, agent, index)),
          ),
        ),
        catchAndLogError<AgentMapMarker[] | null>(of(null)),
      );

    const fetchAgentMarkers = rxMethod<MarkersRequest>(
      pipe(
        filter(() => platformService.isPlatformBrowser),
        tap(({ shape }) =>
          patchState(store, () => ({
            // a shaped request only narrows what is already on screen, so the current markers stay put while it answers
            ...(shape ? {} : { status: 'loading' as const, markers: [] }),
          })),
        ),
        switchMap((request) =>
          loadMarkers(request).pipe(
            tap((markers) =>
              patchState(store, (state) => ({
                status: markers ? ('ready' as const) : ('failed' as const),
                loaded: true,
                markers: markers ?? [],
                ...(!request.shape && markers?.some(({ agentCode }) => !state.hiddenAgentCodes.includes(agentCode))
                  ? refitBounds(state)
                  : {}),
              })),
            ),
          ),
        ),
      ),
    );

    const refetch = (): void => {
      if (store.gated()) {
        return;
      }

      const agents = store.agents().filter(({ agentCode }) => !store.hiddenAgentCodes().includes(agentCode));

      if (!agents.length) {
        patchState(store, { markers: [], status: 'ready', loaded: true });

        return;
      }

      fetchAgentMarkers({ agents, markerType: store.markerType(), shape: store.shape() });
    };

    const load = rxMethod<LoadRequest>(
      pipe(
        tap(({ agents, gated }: LoadRequest) =>
          patchState(store, (state) => {
            const hiddenAgentCodes = state.hiddenAgentCodes.filter((code) => agents.some(({ agentCode }) => agentCode === code));

            return {
              agents,
              gated,
              ...(hiddenAgentCodes.length === state.hiddenAgentCodes.length ? {} : { hiddenAgentCodes }),
            };
          }),
        ),
        // Gated visitors only ever see the static preview, so the markers behind the blur aren't worth fetching,
        // and everyone else waits until the map itself is on screen.
        filter(({ gated, visible }: LoadRequest) => !gated && visible),
        map(({ agents }: LoadRequest) => agents),
        distinctUntilChanged(sameAgentCodes),
        tap(() => {
          patchState(store, { shape: undefined });
          refetch();
        }),
      ),
    );

    const loadMarkerInfo = (markerType: MapMarkerType, codes: string[]): Observable<MarkerInfoType> =>
      markerType === 'Review'
        ? reviewApiService.searchReviews({ verticalTypes: [SALES], reviewCodes: codes }).pipe(map(({ results }) => results))
        : campaignsApiService
            .getSalesCampaignsByCode(codes)
            .pipe(map((campaigns) => campaigns.map((campaign) => mapCampaignSummaryToPropertyTile(campaign, 'property'))));

    const selectMarker = rxMethod<RmaMapMarkerDetail>(
      pipe(
        tap(({ id }) =>
          patchState(store, (state) => {
            const previous = state.markers.find(({ detail }) => detail.status === 'active');
            const markers = previous ? withStatus(state.markers, previous.detail.id, 'viewed') : state.markers;

            return { markers: withStatus(markers, id, 'active') };
          }),
        ),
        filter(({ info }) => !info),
        switchMap(({ id, codes, markerType }) =>
          loadMarkerInfo(markerType, codes).pipe(
            tapResponse({
              next: (info) => patchState(store, (state) => ({ markers: withInfo(state.markers, id, info) })),
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState(store, (state) => ({ markers: withStatus(state.markers, id, 'viewed') }));
              },
            }),
          ),
        ),
      ),
    );

    return {
      load,
      selectMarker,
      setMarkerType(markerType: MapMarkerType): void {
        if (store.markerType() === markerType) {
          return;
        }

        patchState(store, { markerType });
        refetch();
      },
      setShape(shape: SelectionShape): void {
        patchState(store, { shape });
        refetch();
      },
      toggle(agentCode: string): void {
        patchState(store, (state) => ({
          hiddenAgentCodes: state.hiddenAgentCodes.includes(agentCode)
            ? state.hiddenAgentCodes.filter((code) => code !== agentCode)
            : [...state.hiddenAgentCodes, agentCode],
        }));

        refetch();
      },
      deselectMarker(id: string): void {
        patchState(store, (state) => ({ markers: withStatus(state.markers, id, 'viewed') }));
      },
    };
  }),
);
